import { ImageOff } from "lucide-react";
import { type ChangeEvent, type DragEvent, useRef, useState } from "react";
import { toast } from "sonner";
import {
  type ImagerySlotData,
  type ImagerySlotName,
  useMoveImage,
  useUploadSlotImage,
} from "../../../../hooks/use-imagery";
import { cn } from "../../../../lib/utils";
import { LoadingSpinner } from "../../../ui/loading";
import { getImageDragRef, setImageDragRef } from "./drag-payload";

interface Props {
  slotName: ImagerySlotName;
  label: string;
  recordId: string | undefined;
  slot: ImagerySlotData | null;
}

export function ImagerySlotCard({ slotName, label, recordId, slot }: Props): React.ReactElement {
  const uploadSlot = useUploadSlotImage(recordId);
  const moveImage = useMoveImage(recordId);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const disabled = !recordId;

  const handleFileSelected = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !recordId) return;

    uploadSlot.mutate(
      { slot: slotName, file },
      {
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : `Failed to upload ${label.toLowerCase()}`);
        },
      },
    );
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setIsDragOver(false);
    if (!recordId) return;

    const ref = getImageDragRef(e);
    if (!ref) return;
    if ("slot" in ref && ref.slot === slotName) return;

    moveImage.mutate(
      { from: ref, to: { slot: slotName } },
      {
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : "Failed to move image");
        },
      },
    );
  };

  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground mb-1">{label}</p>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: drag-and-drop target, replace via the button below */}
      <div
        draggable={!!slot}
        onDragStart={(e) => {
          if (slot) setImageDragRef(e, { slot: slotName });
        }}
        onDragOver={handleDragOver}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
        className={cn(
          "h-40 rounded-md border-2 border-dashed flex items-center justify-center text-sm text-muted-foreground transition-colors overflow-hidden",
          isDragOver ? "border-primary bg-primary/5" : "border-input bg-muted/30",
        )}
        data-testid={`imagery-slot-${slotName}`}
      >
        {uploadSlot.isPending ? (
          <LoadingSpinner />
        ) : slot ? (
          <img src={slot.url} alt={label} className="h-full w-full object-cover" />
        ) : (
          <div className="flex flex-col items-center gap-1">
            <ImageOff className="h-5 w-5" />
            No image set.
          </div>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileSelected}
        className="hidden"
        data-testid={`imagery-slot-file-input-${slotName}`}
      />
      <button
        type="button"
        disabled={disabled || uploadSlot.isPending}
        onClick={() => fileInputRef.current?.click()}
        className="mt-1 text-xs font-medium text-primary hover:underline disabled:opacity-50 disabled:no-underline"
        data-testid={`imagery-slot-replace-${slotName}`}
      >
        {slot ? "Replace" : "Upload"}
      </button>
      {disabled && <p className="text-xs text-muted-foreground mt-1">Save the record first.</p>}
    </div>
  );
}
